import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * BreadcrumbSchema Component
 * 
 * Automatically generates breadcrumb schema markup based on the current route.
 * 
 * @param {Object} props
 * @param {string} props.customTitle - Optional custom title for the current page (useful for blog posts)
 * @param {Object} props.customNames - Optional object to override default segment names
 */
const BreadcrumbSchema = ({ customTitle = null, customNames = {} }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Base URL
  const baseUrl = 'https://mhcockpit.com';

  // Custom name mappings for specific routes
  const defaultNameMap = {
    'training': 'Training',
    'private-pilot-license': 'Private Pilot License',
    'commercial-pilot-license': 'Commercial Pilot License',
    'type-rating': 'Type Rating',
    'airline-transport-pilot-license': 'Airline Transport Pilot License',
    'dgca-license-conversion': 'DGCA License Conversion',
    'dgca-medical-requirements': 'DGCA Medical Requirements',
    'dgca-pilot-computer-number': 'DGCA Pilot Computer Number',
    'electronic-personnel-license-epl': 'DGCA Electronic Personnel Licence (EPL)',
    'bba-aviation-management': 'BBA Aviation Management',
    'bba-aviation-and-ground-handling': 'BBA Aviation and Ground Handling',
    'bsc-aviation': 'BSc Aviation',
    'btech-aeronautical-engineering': 'B.Tech Aeronautical Engineering',
    'bsc-aeronautical-science': 'BSc Aeronautical Science',
    'mba-aviation-and-airport-management': 'MBA Aviation and Airport Management',
    'cabin-crew': 'Cabin Crew',
    'pg-diploma-in-aircraft-maintenance': 'PG Diploma in Aircraft Maintenance',
    'diploma-in-airport-management': 'Diploma in Airport Management',
    'innovation': 'Innovation',
    'flight-simulator': 'Flight Simulator',
    'cessna': 'Cessna',
    'airbus': 'Airbus',
    'boeing': 'Boeing',
    'fixed-wing': 'Fixed Wing',
    'colleges': 'Colleges',
    'for-universities': 'For Universities',
    'vels-university': 'Vels University',
    'alliance-university': 'Alliance University',
    'bharath-university': 'Bharath University',
    'crescent-university': 'Crescent University',
    'bishop-heber-college': 'Bishop Heber College',
    'jamal-mohamed-college': 'Jamal Mohamed College',
    'careers': 'Careers',
    'pilot-ground-instructor': 'Pilot Ground Instructor',
    'ame-instructor': 'AME Instructor',
    'cabin-crew-instructor': 'Cabin Crew Instructor',
    'ground-operations-trainer': 'Ground Operations Trainer',
    'flight-dispatch': 'Flight Dispatch',
    'it': 'IT',
    'drone-pilots': 'Drone Pilots',
    'bdm': 'BDM',
    'media': 'Media',
    'blogs': 'Blogs',
    'gallery': 'Gallery',
    'about-us': 'About Us',
    'our-governance': 'Our Governance',
    'our-values': 'Our Values',
    'our-presence': 'Our Presence',
    'students': 'Students',
    'campus': 'Campus',
    'dgca-exams-cleared': 'DGCA Exams Cleared',
    'contact': 'Contact',
    'communication-policy': 'Communication Policy',
    'terms-and-conditions': 'Terms and Conditions',
    'privacy-policy': 'Privacy Policy',
    'consulting': 'Consulting',
    'lab-infrastructure': 'Lab Infrastructure',
    'aircraft-leasing-and-financing': 'Aircraft Leasing and Financing'
  };

  // Merge default names with custom names
  const nameMap = { ...defaultNameMap, ...customNames };

  // Function to get display name for a segment
  const getDisplayName = (segment, isLast = false) => {
    // If it's the last segment and customTitle is provided, use it
    if (isLast && customTitle) {
      return customTitle;
    }
    
    // Check if custom mapping exists
    if (nameMap[segment]) {
      return nameMap[segment];
    }
    
    // Otherwise, capitalize and format
    return segment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Function to generate breadcrumb schema based on pathname
  const generateBreadcrumbSchema = () => {
    // Remove trailing slash and split path into segments
    const segments = pathname.replace(/\/$/, '').split('/').filter(Boolean);
    
    // Base breadcrumb (Home)
    const breadcrumbs = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      }
    ];

    // If homepage, return just home breadcrumb
    if (segments.length === 0) {
      return breadcrumbs;
    }

    // Build breadcrumbs dynamically
    let currentPath = baseUrl;
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;
      
      breadcrumbs.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": getDisplayName(segment, isLast),
        "item": currentPath
      });
    });

    return breadcrumbs;
  };

  // Generate the schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": generateBreadcrumbSchema()
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema)
      }}
    />
  );
};

export default BreadcrumbSchema;